import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuth } from '@/hooks/useAuth';
import { api, ApiError, type Me } from '@/lib/api';

export function SettingsPage() {
  const { state, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (state.status !== 'authed') return null;
  const s = state.me.settings;

  const toggle = async (
    key: 'proactive_mode' | 'save_video' | 'save_audio',
    value: boolean,
  ) => {
    setSaving(true);
    try {
      await api.patch<Me['settings']>('/api/settings', { [key]: value ? 1 : 0 });
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col px-5 pt-5 pb-16">
      <header className="flex items-center justify-between mb-4">
        <Link to="/" className="btn-ghost gap-1 -ml-2">
          <BackIcon /> Назад
        </Link>
        <h1 className="text-[15px] font-semibold">Настройки</h1>
        <span className="size-8" />
      </header>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <Card>
          <Row
            title="Подсказки в realtime"
            sub="AI сам предлагает вопросы и ответы во время встречи"
          >
            <Switch
              checked={!!s?.proactive_mode}
              onChange={(v) => toggle('proactive_mode', v)}
              disabled={saving}
            />
          </Row>
          <Divider />
          <Row title="Сохранять аудио" sub="Файл встречи на сервере">
            <Switch
              checked={!!s?.save_audio}
              onChange={(v) => toggle('save_audio', v)}
              disabled={saving}
            />
          </Row>
          <Divider />
          <Row title="Сохранять видео" sub="Записывать кадры с камеры">
            <Switch
              checked={!!s?.save_video}
              onChange={(v) => toggle('save_video', v)}
              disabled={saving}
            />
          </Row>
        </Card>

        <Card>
          <ChangePinForm onDone={(t) => setMsg(t)} />
          {msg && <p className="mt-3 text-sm text-success">{msg}</p>}
        </Card>

        <Card className="text-center">
          <Button
            variant="outline"
            full
            onClick={async () => {
              await logout();
              nav('/login', { replace: true });
            }}
          >
            Выйти из аккаунта
          </Button>
        </Card>

        <p className="text-center text-[11px] text-ink-3 mt-6">
          eye.laba.bot · {state.me.user.display_name}
        </p>
      </motion.div>
    </div>
  );
}

function Row({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div>
        <div className="text-[15px] font-medium">{title}</div>
        {sub && <div className="text-xs text-ink-3 mt-0.5">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-white/[0.06] my-3" />;
}

function Switch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative h-7 w-12 rounded-pill transition focus-ring',
        checked ? 'bg-accent shadow-glow' : 'bg-white/10',
        disabled && 'opacity-50',
      )}
    >
      <span
        className={clsx(
          'absolute top-0.5 size-6 rounded-full bg-white transition-all',
          checked ? 'left-[22px]' : 'left-0.5',
        )}
      />
    </button>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChangePinForm({ onDone }: { onDone: (msg: string) => void }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!/^\d{4,8}$/.test(next)) {
      setErr('PIN должен быть 4–8 цифр');
      return;
    }
    if (next !== confirm) {
      setErr('PIN не совпадает');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/change-pin', { currentPin: cur, newPin: next });
      onDone('PIN обновлён');
      setCur('');
      setNext('');
      setConfirm('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="text-[15px] font-medium">Сменить PIN</div>
      <input
        className="input-base"
        type="password"
        inputMode="numeric"
        autoComplete="current-password"
        placeholder="Текущий PIN"
        value={cur}
        onChange={(e) => setCur(e.target.value.replace(/\D/g, ''))}
      />
      <input
        className="input-base"
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        placeholder="Новый PIN"
        value={next}
        onChange={(e) => setNext(e.target.value.replace(/\D/g, ''))}
      />
      <input
        className="input-base"
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        placeholder="Повторите новый PIN"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
      />
      {err && <div className="text-sm text-danger">{err}</div>}
      <Button type="submit" full disabled={busy || !cur || !next}>
        Сохранить
      </Button>
    </form>
  );
}
